/**
 * LMS Firestore integration - submissions + user progress.
 * See docs/FIREBASE_DATA_INTEGRATION_PLAN.md
 *
 * Submissions: mostly create-only from client. Module 5 poll uses `fetchMetaReflexivityPollAggregates` (read) when rules allow.
 * User doc: users/{uid} - read once per session in AuthContext (Phase 3).
 */
import {
  collection,
  addDoc,
  doc,
  getDoc,
  getDocs,
  query,
  where,
  setDoc,
  updateDoc,
  deleteDoc,
  deleteField,
  serverTimestamp,
  arrayUnion,
} from 'firebase/firestore';
import { db, auth, isFirebaseConfigured } from '../firebase';

export function isLmsFirestoreReady() {
  return !!db && isFirebaseConfigured();
}

function currentUid() {
  try {
    return auth?.currentUser?.uid ?? null;
  } catch {
    return null;
  }
}

/** First token of a one-word reflection, normalized for word-cloud aggregation. */
export function normalizeReflectionCloudWord(text) {
  const word = String(text || '').trim().split(/\s+/)[0];
  if (!word) return '';
  return word.toLowerCase();
}

/**
 * One-word reflection (word cloud) - workplace Anika step (merged story + reflect).
 * Requires a signed-in user so submissions are attributable.
 * @returns {Promise<boolean>}
 */
export async function writeReflectionSubmission(text, moduleId) {
  if (!isLmsFirestoreReady()) return false;
  const trimmed = (text || '').trim();
  if (!trimmed) return false;
  const uid = currentUid();
  if (!uid) {
    console.warn('[lmsData] writeReflectionSubmission skipped: no signed-in user');
    return false;
  }
  try {
    await addDoc(collection(db, 'reflection_submissions'), {
      text: trimmed,
      moduleId: moduleId || 'workplace-module',
      userId: uid,
      createdAt: serverTimestamp(),
    });
    return true;
  } catch (e) {
    console.warn('[lmsData] writeReflectionSubmission', e);
    return false;
  }
}

/**
 * Aggregate one-word reflection submissions for the Part 2 word cloud.
 * Each stored submission increments its first word; duplicates from the same learner are counted separately.
 * Requires Firestore rules to allow read for signed-in users on `reflection_submissions`.
 * @param {string} [forModuleId='workplace-module']
 * @returns {Promise<{ counts: Record<string, number>, total: number } | null>}
 */
export async function fetchReflectionWordCloudAggregates(forModuleId = 'workplace-module') {
  if (!isLmsFirestoreReady()) return null;
  try {
    const snap = await getDocs(collection(db, 'reflection_submissions'));
    /** @type {Record<string, number>} */
    const counts = {};
    let total = 0;
    snap.forEach((d) => {
      const data = d.data();
      const mid = data?.moduleId;
      if (forModuleId === 'workplace-module') {
        if (mid && mid !== 'workplace-module') return;
      } else if (mid !== forModuleId) {
        return;
      }
      const word = normalizeReflectionCloudWord(data?.text);
      if (!word) return;
      counts[word] = (counts[word] || 0) + 1;
      total += 1;
    });
    return { counts, total };
  } catch (e) {
    console.warn('[lmsData] fetchReflectionWordCloudAggregates', e);
    return null;
  }
}

/**
 * Parent module: Pause or Act story responses (Sara / Anika / Aman sections).
 * Always stores `userId` for per-user retrieval / reset.
 */
export async function writeParentModuleStoryResponse({ storyKey, responseType, text, moduleId = 'parent-module' }) {
  if (!isLmsFirestoreReady()) return false;
  const trimmed = String(text || '').trim();
  if (!trimmed || !storyKey || !responseType) return false;
  const uid = currentUid();
  if (!uid) {
    console.warn('[lmsData] writeParentModuleStoryResponse skipped: no signed-in user');
    return false;
  }
  try {
    await addDoc(collection(db, 'parent_module_responses'), {
      storyKey: String(storyKey),
      responseType: String(responseType),
      text: trimmed,
      cloudWord: normalizeReflectionCloudWord(trimmed),
      moduleId: moduleId || 'parent-module',
      userId: uid,
      createdAt: serverTimestamp(),
    });
    return true;
  } catch (e) {
    console.warn('[lmsData] writeParentModuleStoryResponse', e);
    return false;
  }
}

/**
 * Aggregate first-word counts for a parent story Pause word cloud.
 * @param {string} storyKey - sara | anika | aman
 * @param {string} [forModuleId='parent-module']
 */
export async function fetchParentPauseWordCloudAggregates(storyKey, forModuleId = 'parent-module') {
  if (!isLmsFirestoreReady() || !storyKey) return null;
  try {
    const snap = await getDocs(collection(db, 'parent_module_responses'));
    /** @type {Record<string, number>} */
    const counts = {};
    let total = 0;
    snap.forEach((d) => {
      const data = d.data();
      if ((data?.moduleId || 'parent-module') !== forModuleId) return;
      if (String(data?.storyKey || '') !== String(storyKey)) return;
      if (String(data?.responseType || '') !== 'pause') return;
      const word = normalizeReflectionCloudWord(data?.cloudWord || data?.text);
      if (!word) return;
      counts[word] = (counts[word] || 0) + 1;
      total += 1;
    });
    return { counts, total };
  } catch (e) {
    console.warn('[lmsData] fetchParentPauseWordCloudAggregates', e);
    return null;
  }
}

/**
 * Latest Pause / Act text per story for the signed-in user (for restore across sessions).
 * @returns {Promise<{ pause: Record<string, string>, act: Record<string, string> } | null>}
 */
export async function fetchLatestParentStoryResponsesForUser(forModuleId = 'parent-module') {
  if (!isLmsFirestoreReady()) return null;
  const uid = currentUid();
  if (!uid) return null;
  try {
    const q = query(collection(db, 'parent_module_responses'), where('userId', '==', uid));
    const snap = await getDocs(q);
    /** @type {Record<string, { text: string, at: number }>} */
    const latestPause = {};
    /** @type {Record<string, { text: string, at: number }>} */
    const latestAct = {};
    snap.forEach((d) => {
      const data = d.data();
      if ((data?.moduleId || 'parent-module') !== (forModuleId || 'parent-module')) return;
      const storyKey = String(data?.storyKey || '');
      const responseType = String(data?.responseType || '');
      const text = String(data?.text || '').trim();
      if (!storyKey || !text) return;
      const created = data?.createdAt?.toMillis?.() || 0;
      const bucket = responseType === 'act' ? latestAct : responseType === 'pause' ? latestPause : null;
      if (!bucket) return;
      if (!bucket[storyKey] || created >= bucket[storyKey].at) {
        bucket[storyKey] = { text, at: created };
      }
    });
    /** @type {Record<string, string>} */
    const pause = {};
    /** @type {Record<string, string>} */
    const act = {};
    Object.entries(latestPause).forEach(([k, v]) => {
      pause[k] = v.text;
    });
    Object.entries(latestAct).forEach(([k, v]) => {
      act[k] = v.text;
    });
    return { pause, act };
  } catch (e) {
    console.warn('[lmsData] fetchLatestParentStoryResponsesForUser', e);
    return null;
  }
}

/**
 * Workshop interest form after reflection.
 */
export async function writeWorkshopEnquiry({ name, email, organization }) {
  if (!isLmsFirestoreReady()) return;
  try {
    await addDoc(collection(db, 'workshop_enquiries'), {
      name: String(name || '').trim(),
      email: String(email || '').trim(),
      organization: String(organization || '').trim(),
      userId: currentUid(),
      createdAt: serverTimestamp(),
    });
  } catch (e) {
    console.warn('[lmsData] writeWorkshopEnquiry', e);
  }
}

/** Valid moment option keys stored in `meta_reflexivity_responses` (Module 5 poll). */
const META_REFLEXIVITY_MOMENT_TYPES = new Set(['hiring', 'promotion', 'mentorship', 'sponsorship', 'other']);

function normalizeMetaReflexivityMomentType(value) {
  const raw = String(value || '').trim() || 'other';
  return META_REFLEXIVITY_MOMENT_TYPES.has(raw) ? raw : 'other';
}

/** Dedupe + filter to allowed keys; default to `['other']` if nothing valid. */
function normalizeMetaReflexivityMomentTypesList(values) {
  const seen = new Set();
  const out = [];
  for (const v of Array.isArray(values) ? values : []) {
    const t = normalizeMetaReflexivityMomentType(v);
    if (!seen.has(t)) {
      seen.add(t);
      out.push(t);
    }
  }
  return out.length ? out : ['other'];
}

/** Read types from a stored doc: prefer `momentTypes` array, else legacy `momentType` string. */
function getMomentTypesFromDocData(data) {
  if (Array.isArray(data?.momentTypes) && data.momentTypes.length > 0) {
    return normalizeMetaReflexivityMomentTypesList(data.momentTypes);
  }
  if (data?.momentType != null && String(data.momentType).trim() !== '') {
    return [normalizeMetaReflexivityMomentType(data.momentType)];
  }
  return ['other'];
}

/**
 * Meta-reflexivity moment survey before PDF download (Module 5).
 * Pass `momentTypes` (string[]). Legacy callers may pass a single-type array only.
 */
export async function writeMetaReflexivityResponse({ momentTypes, otherText, moduleId } = {}) {
  if (!isLmsFirestoreReady()) return false;
  const normalized = normalizeMetaReflexivityMomentTypesList(momentTypes);
  if (!normalized.length) return false;
  const uid = currentUid();
  if (!uid) {
    console.warn('[lmsData] writeMetaReflexivityResponse skipped: no signed-in user');
    return false;
  }
  try {
    await addDoc(collection(db, 'meta_reflexivity_responses'), {
      momentTypes: normalized,
      otherText: otherText != null && String(otherText).trim() ? String(otherText).trim() : null,
      moduleId: moduleId || 'workplace-module',
      userId: uid,
      createdAt: serverTimestamp(),
    });
    return true;
  } catch (e) {
    console.warn('[lmsData] writeMetaReflexivityResponse', e);
    return false;
  }
}

/**
 * Aggregate Module 5 moment-type selections for an anonymous poll in the UI.
 * Each document may include multiple types (`momentTypes`); each type increments its count.
 * `total` is the number of submitted responses (documents). Percentages are share of respondents who included that option (can sum to >100%).
 * Legacy docs with only `momentType` are counted as a single selection.
 * Requires Firestore rules to allow read for signed-in users, e.g.:
 *   match /meta_reflexivity_responses/{docId} {
 *     allow create: if true;
 *     allow read: if request.auth != null;
 *     allow update, delete: if false;
 *   }
 * @param {string} [forModuleId='workplace-module'] Only aggregate docs for this module (legacy docs without moduleId count as workplace).
 */
export async function fetchMetaReflexivityPollAggregates(forModuleId = 'workplace-module') {
  if (!isLmsFirestoreReady()) return null;
  try {
    const snap = await getDocs(collection(db, 'meta_reflexivity_responses'));
    const counts = {
      hiring: 0,
      promotion: 0,
      mentorship: 0,
      sponsorship: 0,
      other: 0,
    };
    let total = 0;
    snap.forEach((d) => {
      const data = d.data();
      const mid = data?.moduleId;
      if (forModuleId === 'workplace-module') {
        if (mid && mid !== 'workplace-module') return;
      } else if (mid !== forModuleId) {
        return;
      }
      const types = getMomentTypesFromDocData(data);
      types.forEach((t) => {
        counts[t] = (counts[t] || 0) + 1;
      });
      total += 1;
    });
    return { counts, total };
  } catch (e) {
    console.warn('[lmsData] fetchMetaReflexivityPollAggregates', e);
    return null;
  }
}

/** Valid keys for five-barrier recognition polls (educator + community). */
const EDUCATOR_BARRIER_POLL_KEYS = new Set([
  'institutional-failure',
  'sponsorship-scarcity',
  'informal-gatekeeping',
  'transition-fragility',
  'discretion-authority',
]);

const COMMUNITY_BARRIER_POLL_KEYS = new Set([
  'community-legitimacy',
  'mentorship-scarcity',
  'cultural-fit-filter',
  'scrutiny-nonlinear-paths',
  'community-norms-autonomy',
]);

function barrierPollKeysForModule(moduleId) {
  return moduleId === 'community-module' ? COMMUNITY_BARRIER_POLL_KEYS : EDUCATOR_BARRIER_POLL_KEYS;
}

function emptyBarrierPollCounts(moduleId) {
  const counts = {};
  barrierPollKeysForModule(moduleId).forEach((k) => {
    counts[k] = 0;
  });
  return counts;
}

function normalizeEducatorBarrierKeysList(values, moduleId = 'educator-module') {
  const validKeys = barrierPollKeysForModule(moduleId);
  const seen = new Set();
  const out = [];
  for (const v of Array.isArray(values) ? values : []) {
    const raw = String(v || '').trim();
    if (!validKeys.has(raw) || seen.has(raw)) continue;
    seen.add(raw);
    out.push(raw);
  }
  return out;
}

/** Educator module: which barriers feel most familiar (multi-select). */
export async function writeEducatorBarrierRecognitionResponse({ barrierKeys, moduleId } = {}) {
  if (!isLmsFirestoreReady()) return false;
  const mid = moduleId || 'educator-module';
  const normalized = normalizeEducatorBarrierKeysList(barrierKeys, mid);
  if (!normalized.length) return false;
  const uid = currentUid();
  if (!uid) {
    console.warn('[lmsData] writeEducatorBarrierRecognitionResponse skipped: no signed-in user');
    return false;
  }
  try {
    await addDoc(collection(db, 'educator_barrier_recognition_responses'), {
      barrierKeys: normalized,
      moduleId: mid,
      userId: uid,
      createdAt: serverTimestamp(),
    });
    return true;
  } catch (e) {
    console.warn('[lmsData] writeEducatorBarrierRecognitionResponse', e);
    return false;
  }
}

/** Aggregate five-barrier recognition poll for UI (educator or community module). */
export async function fetchEducatorBarrierRecognitionAggregates(moduleId = 'educator-module') {
  if (!isLmsFirestoreReady()) return null;
  const mid = moduleId || 'educator-module';
  try {
    const snap = await getDocs(collection(db, 'educator_barrier_recognition_responses'));
    const counts = emptyBarrierPollCounts(mid);
    let total = 0;
    snap.forEach((d) => {
      const data = d.data();
      if (data?.moduleId && data.moduleId !== mid) return;
      const keys = normalizeEducatorBarrierKeysList(data?.barrierKeys, mid);
      if (!keys.length) return;
      keys.forEach((k) => {
        counts[k] = (counts[k] || 0) + 1;
      });
      total += 1;
    });
    return { counts, total };
  } catch (e) {
    console.warn('[lmsData] fetchEducatorBarrierRecognitionAggregates', e);
    return null;
  }
}

/**
 * Small Actions (Module 7): one intentional shift commitment statement.
 * Requires a signed-in user so commitments are attributable.
 * @returns {Promise<boolean>}
 */
export async function writeIntentionalShiftCommitment({ moduleId, text }) {
  if (!isLmsFirestoreReady()) return false;
  const trimmed = String(text || '').trim();
  if (!trimmed) return false;
  const uid = currentUid();
  if (!uid) {
    console.warn('[lmsData] writeIntentionalShiftCommitment skipped: no signed-in user');
    return false;
  }
  try {
    await addDoc(collection(db, 'intentional_shift_commitments'), {
      moduleId: moduleId || 'workplace-module',
      text: trimmed,
      userId: uid,
      createdAt: serverTimestamp(),
    });
    return true;
  } catch (e) {
    console.warn('[lmsData] writeIntentionalShiftCommitment', e);
    return false;
  }
}

/**
 * Workplace Module 8 survey response.
 */
export async function writeWorkplaceSurveyResponse(payload) {
  if (!isLmsFirestoreReady()) return false;
  if (!payload || typeof payload !== 'object') return false;
  const uid = currentUid();
  if (!uid) {
    console.warn('[lmsData] writeWorkplaceSurveyResponse skipped: no signed-in user');
    return false;
  }
  try {
    await addDoc(collection(db, 'workplace_module_surveys'), {
      ...payload,
      moduleId: payload.moduleId || 'workplace-module',
      userId: uid,
      createdAt: serverTimestamp(),
    });
    return true;
  } catch (e) {
    console.warn('[lmsData] writeWorkplaceSurveyResponse', e);
    return false;
  }
}

/**
 * Post-survey participant review (workplace module certificate step).
 * Firestore: `workplace_module_participant_reviews` - add create rules for signed-in users.
 */
export async function writeWorkplaceParticipantReview(payload) {
  if (!isLmsFirestoreReady()) return false;
  if (!payload || typeof payload !== 'object') return false;
  const uid = currentUid();
  if (!uid) {
    console.warn('[lmsData] writeWorkplaceParticipantReview skipped: no signed-in user');
    return false;
  }
  try {
    await addDoc(collection(db, 'workplace_module_participant_reviews'), {
      ...payload,
      moduleId: payload.moduleId || 'workplace-module',
      userId: uid,
      createdAt: serverTimestamp(),
    });
    return true;
  } catch (e) {
    console.warn('[lmsData] writeWorkplaceParticipantReview', e);
    return false;
  }
}

/**
 * Pre-module research consent supplement (Building Workplace Bridges enrollment).
 * Replaces a separate consent document: yes/no + optional text fields after user opts into research.
 * Firestore collection: `research_consent_supplements` - add matching security rules (create for signed-in users).
 *
 * Pass `{ consentOnly: true, moduleId }` when the Step 2 questionnaire is deferred: writes a short doc with
 * `enrollmentResearchConsentAccepted` and `consentSupplementQuestionnaireSkipped`.
 */
/** @returns {Promise<{ ok: boolean, skipped?: boolean }>} */
export async function writeResearchConsentSupplement(data) {
  if (!isLmsFirestoreReady()) return { ok: true, skipped: true };
  if (!data || typeof data !== 'object') return { ok: false };
  try {
    const moduleId = data.moduleId || 'workplace-module';
    const base = {
      moduleId,
      userId: currentUid(),
      createdAt: serverTimestamp(),
    };
    if (data.consentOnly) {
      await addDoc(collection(db, 'research_consent_supplements'), {
        ...base,
        enrollmentResearchConsentAccepted: true,
        consentSupplementQuestionnaireSkipped: true,
      });
    } else {
      await addDoc(collection(db, 'research_consent_supplements'), {
        ...base,
        audioTapedPermission: data.audioTapedPermission,
        videoTapedPermission: data.videoTapedPermission,
        wishRemainAnonymous: data.wishRemainAnonymous,
        wishAnonymousWithPseudonym: data.wishAnonymousWithPseudonym,
        pseudonym: data.pseudonym != null ? String(data.pseudonym).trim() || null : null,
        quoteWithNamePermission: data.quoteWithNamePermission,
        receiveStudySummary: data.receiveStudySummary,
        summaryContactInfo: data.summaryContactInfo != null ? String(data.summaryContactInfo).trim() || null : null,
        followUpInterviewInterest: data.followUpInterviewInterest,
        participantPrintedName: String(data.participantPrintedName || '').trim() || null,
        participantDate: String(data.participantDate || '').trim(),
      });
    }
    return { ok: true };
  } catch (e) {
    console.warn('[lmsData] writeResearchConsentSupplement', e);
    return { ok: false };
  }
}

/**
 * Load user progress doc - call once after login (Phase 3: AuthContext).
 */
export async function getUserProgress(uid) {
  if (!db || !uid) return null;
  try {
    const snap = await getDoc(doc(db, 'users', uid));
    if (!snap.exists()) {
      return {
        enrolledModules: [],
        completedModules: [],
        completedSections: {},
      };
    }
    const data = snap.data();
    return {
      enrolledModules: Array.isArray(data.enrolledModules) ? data.enrolledModules : [],
      completedModules: Array.isArray(data.completedModules) ? data.completedModules : [],
      completedSections:
        data.completedSections && typeof data.completedSections === 'object' ? data.completedSections : {},
      updatedAt: data.updatedAt ?? null,
    };
  } catch (e) {
    console.warn('[lmsData] getUserProgress', e);
    return null;
  }
}

/**
 * Store Auth email + display name on users/{uid} (merge) so admin Excel export can show participant identity.
 * Called after sign-in / profile updates. Requires rules that allow the user to write their own users/{uid} doc.
 */
export async function syncUserExportFieldsToFirestore(uid, email, displayName) {
  if (!isLmsFirestoreReady() || !uid) return;
  const emailStr = email != null ? String(email).trim() : '';
  const nameStr = displayName != null ? String(displayName).trim() : '';
  if (!emailStr && !nameStr) return;
  try {
    await setDoc(
      doc(db, 'users', uid),
      {
        exportAuthEmail: emailStr || null,
        exportAuthDisplayName: nameStr || null,
        exportAuthSyncedAt: serverTimestamp(),
      },
      { merge: true }
    );
  } catch (e) {
    console.warn('[lmsData] syncUserExportFieldsToFirestore', e);
  }
}

export async function enrolModule(uid, moduleId) {
  if (!db || !uid || !moduleId) return;
  try {
    await setDoc(
      doc(db, 'users', uid),
      {
        enrolledModules: arrayUnion(moduleId),
        updatedAt: serverTimestamp(),
      },
      { merge: true }
    );
  } catch (e) {
    console.warn('[lmsData] enrolModule', e);
  }
}

/**
 * Append module id to completedModules.
 */
export async function completeModule(uid, moduleId) {
  if (!db || !uid || !moduleId) return;
  try {
    await setDoc(
      doc(db, 'users', uid),
      {
        completedModules: arrayUnion(moduleId),
        updatedAt: serverTimestamp(),
      },
      { merge: true }
    );
  } catch (e) {
    console.warn('[lmsData] completeModule', e);
  }
}

/**
 * Read all module progress docs for a user.
 */
export async function getAllModuleProgress(uid) {
  if (!db || !uid) return [];
  try {
    const colRef = collection(db, 'users', uid, 'module_progress');
    const snap = await getDocs(colRef);
    return snap.docs.map((d) => ({ moduleId: d.id, ...d.data() }));
  } catch (e) {
    console.warn('[lmsData] getAllModuleProgress', e);
    return [];
  }
}

function toPercent(completedStagesCount, totalStages) {
  if (!totalStages || totalStages <= 0) return 0;
  return Math.max(0, Math.min(100, Math.round((completedStagesCount / totalStages) * 100)));
}

/**
 * Ensure module is enrolled and started.
 */
export async function ensureModuleEnrollment(uid, moduleId, totalStages = 0) {
  if (!db || !uid || !moduleId) return;
  try {
    const progressRef = doc(db, 'users', uid, 'module_progress', moduleId);
    const snap = await getDoc(progressRef);
    const existing = snap.exists() ? snap.data() : {};
    await setDoc(
      progressRef,
      {
        moduleId,
        status: existing.status === 'completed' ? 'completed' : 'in-progress',
        enrolledAt: existing.enrolledAt || serverTimestamp(),
        startedAt: existing.startedAt || serverTimestamp(),
        lastVisitedAt: serverTimestamp(),
        totalStages: totalStages || existing.totalStages || 0,
        isEnrolled: true,
        isCompleted: existing.status === 'completed' || existing.isCompleted || false,
        updatedAt: serverTimestamp(),
      },
      { merge: true }
    );
  } catch (e) {
    console.warn('[lmsData] ensureModuleEnrollment', e);
  }
}

/**
 * Optimized single write for stage tracking:
 * - tracks current stage reached
 * - optionally marks stage complete
 * - derives progress and completion status
 * - avoids multiple sequential writes per click
 */
/**
 * Mark a module fully complete (100%, certificate eligible, profile status completed).
 */
export async function markModuleProgressCompleted(
  uid,
  moduleId,
  { finalStageId, totalStages = 0, allStageIds = null } = {}
) {
  if (!db || !uid || !moduleId) return;
  try {
    const progressRef = doc(db, 'users', uid, 'module_progress', moduleId);
    const snap = await getDoc(progressRef);
    const existing = snap.exists() ? snap.data() : {};
    const total = totalStages || existing.totalStages || 0;
    const mergedStageIds = Array.isArray(allStageIds) && allStageIds.length
      ? allStageIds
      : Array.isArray(existing.completedStageIds)
        ? existing.completedStageIds
        : finalStageId
          ? [finalStageId]
          : [];

    await setDoc(
      progressRef,
      {
        moduleId,
        status: 'completed',
        isEnrolled: true,
        isCompleted: true,
        progressPercent: 100,
        completedStagesCount: total || mergedStageIds.length,
        totalStages: total,
        completedStageIds: mergedStageIds,
        currentStageId: finalStageId || existing.currentStageId || mergedStageIds[mergedStageIds.length - 1],
        enrolledAt: existing.enrolledAt || serverTimestamp(),
        startedAt: existing.startedAt || serverTimestamp(),
        lastVisitedAt: serverTimestamp(),
        completedAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      },
      { merge: true }
    );
    await completeModule(uid, moduleId);
  } catch (e) {
    console.warn('[lmsData] markModuleProgressCompleted', e);
  }
}

export async function trackModuleStageProgress(
  uid,
  moduleId,
  stageId,
  totalStages = 0,
  stagePosition = null,
  markStageCompleted = false
) {
  if (!db || !uid || !moduleId || !stageId) return;
  try {
    const progressRef = doc(db, 'users', uid, 'module_progress', moduleId);
    const snap = await getDoc(progressRef);
    const existing = snap.exists() ? snap.data() : {};
    const effectiveTotal = totalStages || existing.totalStages || 0;
    const done = Array.isArray(existing.completedStageIds) ? existing.completedStageIds : [];
    const nextDone = markStageCompleted && !done.includes(stageId) ? [...done, stageId] : done;
    const count = nextDone.length;
    const previousPercent = Number(existing.progressPercent) || 0;
    const safePosition = Number(stagePosition) > 0 ? Number(stagePosition) : 1;
    const isSurveyStage =
      stageId === 'workplace-survey'
      || stageId === 'educator-survey'
      || stageId === 'community-survey'
      || stageId === 'parent-survey';
    const completed =
      effectiveTotal > 0
      && (count >= effectiveTotal
        || (markStageCompleted && safePosition >= effectiveTotal && !isSurveyStage));
    const reachedPercent = effectiveTotal > 0 ? Math.round((safePosition / effectiveTotal) * 100) : previousPercent;
    const completionPercent = completed ? 100 : toPercent(count, effectiveTotal);
    const nextPercent = Math.max(previousPercent, reachedPercent, completionPercent);

    await setDoc(
      progressRef,
      {
        moduleId,
        status: completed ? 'completed' : 'in-progress',
        isEnrolled: true,
        isCompleted: completed,
        completedStageIds: nextDone,
        completedStagesCount: count,
        totalStages: effectiveTotal,
        progressPercent: nextPercent,
        currentStageId: stageId,
        enrolledAt: existing.enrolledAt || serverTimestamp(),
        lastVisitedAt: serverTimestamp(),
        completedAt: completed ? serverTimestamp() : null,
        startedAt: existing.startedAt || serverTimestamp(),
        updatedAt: serverTimestamp(),
      },
      { merge: true }
    );
  } catch (e) {
    console.warn('[lmsData] trackModuleStageProgress', e);
  }
}

/**
 * Reset one module's stage tracking to the beginning (e.g. Introduction "start over").
 * Preserves enrollment; clears completed stages and progress percent on the progress doc.
 */
export async function resetModuleProgressToBeginning(uid, moduleId, firstStageId, totalStages = 0) {
  if (!db || !uid || !moduleId || !firstStageId) return;
  try {
    const progressRef = doc(db, 'users', uid, 'module_progress', moduleId);
    await setDoc(
      progressRef,
      {
        moduleId,
        status: 'in-progress',
        isEnrolled: true,
        isCompleted: false,
        completedStageIds: [],
        completedStagesCount: 0,
        totalStages: Number(totalStages) || 0,
        progressPercent: 0,
        currentStageId: firstStageId,
        completedAt: null,
        lastVisitedAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      },
      { merge: true }
    );
  } catch (e) {
    console.warn('[lmsData] resetModuleProgressToBeginning', e);
  }
}

/** Firestore collections keyed by `userId` that reset should wipe for the signed-in user. */
const USER_MODULE_RESPONSE_COLLECTIONS = [
  'reflection_submissions',
  'workshop_enquiries',
  'meta_reflexivity_responses',
  'intentional_shift_commitments',
  'workplace_module_surveys',
  'workplace_module_participant_reviews',
  'educator_barrier_recognition_responses',
  'research_consent_supplements',
  'parent_module_responses',
];

/**
 * Delete all docs in one collection where userId matches uid.
 * Requires Firestore rules: read + delete for resource.data.userId == request.auth.uid.
 */
async function deleteFirestoreDocsForUser(uid, collectionName) {
  if (!db || !uid || !collectionName) return 0;
  try {
    const q = query(collection(db, collectionName), where('userId', '==', uid));
    const snap = await getDocs(q);
    if (snap.empty) return 0;
    await Promise.all(snap.docs.map((d) => deleteDoc(d.ref)));
    return snap.size;
  } catch (e) {
    console.warn(`[lmsData] deleteFirestoreDocsForUser ${collectionName}`, e);
    return 0;
  }
}

/** Remove browser caches that rehydrate module/survey UI after a Firestore reset. */
export const MODULE_RESPONSE_LOCAL_STORAGE_KEYS = [
  'lms-workplace-reflection-submissions',
  'lms-educator-reflection-submissions',
  'lms-workplace-workshop-enquiries',
  'lms-meta-reflexivity-download-responses',
  'lms-workplace-intentional-shift-submissions',
  'lms-educator-intentional-shift-submissions',
  'lms-workplace-module-survey-submissions',
  'lms-educator-module-survey-submissions',
  'lms-workplace-participant-review',
  'lms-educator-participant-review',
  'lms-educator-meta-private-note',
  'lms-community-reflection-submissions',
  'lms-community-intentional-shift-submissions',
  'lms-community-module-survey-submissions',
  'lms-community-participant-review',
  'lms-community-meta-private-note',
  'lms-parent-pause-sara',
  'lms-parent-pause-anika',
  'lms-parent-pause-aman',
  'lms-parent-act-sara',
  'lms-parent-act-anika',
  'lms-parent-act-aman',
  'lms-parent-module-survey-submissions',
  'lms-parent-participant-review',
];

export function clearModuleResponseLocalStorage() {
  if (typeof localStorage === 'undefined') return;
  MODULE_RESPONSE_LOCAL_STORAGE_KEYS.forEach((key) => {
    try {
      localStorage.removeItem(key);
    } catch (_) {
      // no-op
    }
  });
}

/**
 * Dev/testing helper: clear all module tracking for one user.
 * - deletes users/{uid}/module_progress/*
 * - deletes module submissions/surveys/responses in Firestore for this user
 * - clears legacy users/{uid} summary arrays/fields
 */
export async function resetUserModuleProgress(uid) {
  if (!db || !uid) return { ok: false, reason: 'missing-db-or-uid' };
  try {
    const progressCol = collection(db, 'users', uid, 'module_progress');
    const snap = await getDocs(progressCol);
    await Promise.all(snap.docs.map((d) => deleteDoc(d.ref)));

    await Promise.all(
      USER_MODULE_RESPONSE_COLLECTIONS.map((name) => deleteFirestoreDocsForUser(uid, name))
    );

    await updateDoc(doc(db, 'users', uid), {
      enrolledModules: deleteField(),
      completedModules: deleteField(),
      completedSections: deleteField(),
      progressSummary: deleteField(),
      updatedAt: serverTimestamp(),
    });

    return { ok: true };
  } catch (e) {
    console.warn('[lmsData] resetUserModuleProgress', e);
    return { ok: false, reason: 'error', error: e };
  }
}
